import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import TermsModal from './TermsModal';
import ManagedDisclaimer from './ManagedDisclaimer';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setShowResendVerification(false);
    setResendMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShowResendVerification(false);
    setResendMessage('');

    const result = await login(formData.email, formData.password);

    if (!result.success) {
      setError(result.error);
      // Check if this is an email verification error
      if (result.error.toLowerCase().includes('not verified')) {
        setShowResendVerification(true);
      }
    }

    setLoading(false);
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage('');

    try {
      const response = await authAPI.resendVerification(formData.email);
      setResendMessage(response.message);
    } catch (error) {
      setResendMessage('Verification email sent. Please check your inbox.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Left Panel - Brand */}
      <div className="login-brand-panel">
        <div className="login-brand-content animate-fade-up">
          <img
            src="/logo-white.png"
            alt="Drummond Capital Partners"
            className="login-brand-logo mb-4"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <h1 className="login-brand-title mb-3">Drummond Portal</h1>

          {/* Value proposition */}
          <p className="login-brand-subtitle animate-fade-up animate-delay-1">
            Your gateway to comprehensive investment resources and portfolio analysis tools
          </p>

          {/* Feature highlights */}
          <div className="login-brand-features animate-fade-up animate-delay-2">
            <div className="brand-feature-item">
              <div className="feature-icon">📊</div>
              <div className="feature-text">
                <div className="feature-title">Portfolio Analysis</div>
                <div className="feature-desc">Advanced tools for portfolio construction and review</div>
              </div>
            </div>

            <div className="brand-feature-item">
              <div className="feature-icon">📈</div>
              <div className="feature-text">
                <div className="feature-title">Research & Reports</div>
                <div className="feature-desc">Fund research, performance data, and market insights</div>
              </div>
            </div>

            <div className="brand-feature-item">
              <div className="feature-icon">📁</div>
              <div className="feature-text">
                <div className="feature-title">Client Resources</div>
                <div className="feature-desc">Document library, updates, and support materials</div>
              </div>
            </div>
          </div>

          {/* For clients tagline */}
          <p className="login-brand-footer animate-fade-up animate-delay-3">
            Exclusively for Drummond Capital Partners clients
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="login-form-panel">
        <div className="login-card animate-fade-up animate-delay-1">
          {/* Header */}
          <h3>Welcome Back</h3>
          <p className="subtitle">Sign in to access your portal</p>

          {/* Error Alert */}
          {error && (
            <div className="alert alert-danger mb-4" role="alert" style={{ borderRadius: 'var(--radius-md)' }}>
              {error}
              {showResendVerification && (
                <div className="mt-2">
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    style={{ borderRadius: 'var(--radius-sm)' }}
                  >
                    {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Resend Message */}
          {resendMessage && (
            <div className="alert alert-success mb-4" role="alert" style={{ borderRadius: 'var(--radius-md)' }}>
              {resendMessage}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="email" className="form-label-premium">Email Address</label>
              <input
                type="email"
                className="form-input-premium"
                id="email"
                name="email"
                autoComplete="username"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                required
                autoFocus
              />
              <small className="text-muted" style={{ fontSize: '0.8rem' }}>Email is case sensitive</small>
            </div>

            <div className="mb-3">
              <label htmlFor="password" className="form-label-premium">Password</label>
              <input
                type="password"
                className="form-input-premium"
                id="password"
                name="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
              />
            </div>

            <div className="mb-4 text-end">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none"
                onClick={() => navigate('/forgot-password')}
                style={{
                  color: 'var(--gray-color)',
                  fontSize: 'var(--text-sm)',
                  transition: 'color var(--transition-fast)'
                }}
                onMouseOver={(e) => e.target.style.color = 'var(--secondary-color)'}
                onMouseOut={(e) => e.target.style.color = 'var(--gray-color)'}
              >
                Forgot password?
              </button>
            </div>

            {/* Disclaimer Checkbox */}
            <div className="mb-4" style={{
              backgroundColor: 'rgba(7, 32, 64, 0.03)',
              borderRadius: 'var(--radius-md)',
              padding: '12px 14px',
              border: '1px solid rgba(0, 51, 102, 0.1)'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                cursor: 'pointer',
                margin: 0
              }}>
                <input
                  type="checkbox"
                  checked={disclaimerAccepted}
                  onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                  style={{
                    marginTop: '3px',
                    accentColor: 'var(--primary-color)',
                    width: '16px',
                    height: '16px',
                    flexShrink: 0
                  }}
                />
                <span style={{
                  color: 'var(--gray-color)',
                  fontSize: 'var(--text-xs)',
                  lineHeight: 1.5
                }}>
                  <ManagedDisclaimer dkey="portal_login_consent" as="span" />{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: 'var(--primary-color)',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: 'inherit',
                      fontFamily: 'inherit'
                    }}
                  >
                    Disclaimer &amp; Terms
                  </button>.
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="btn-premium"
              disabled={loading || !disclaimerAccepted}
              title={!disclaimerAccepted ? 'Please accept the disclaimer to continue' : ''}
              style={{ opacity: !disclaimerAccepted ? 0.6 : 1 }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Signing in...
                </>
              ) : (
                'Confirm and Continue'
              )}
            </button>
          </form>

          {/* Switch to Register */}
          <div className="text-center mt-4 pt-3" style={{ borderTop: '1px solid rgba(0, 51, 102, 0.08)' }}>
            <p style={{ color: 'var(--gray-color)', fontSize: 'var(--text-sm)', marginBottom: 0 }}>
              Don't have an account?{' '}
              <button
                className="btn btn-link p-0 text-decoration-none"
                onClick={() => navigate('/register')}
                style={{
                  color: 'var(--primary-color)',
                  fontWeight: 600,
                  fontSize: 'var(--text-sm)'
                }}
              >
                Create Account
              </button>
            </p>
          </div>
        </div>
      </div>

      <TermsModal show={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </div>
  );
};

export default Login;
